import { useMemo } from 'react';
import { Button, Flex } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier.tsx';
import { Select, TextInput } from 'react-hook-form-mantine';
import { FormProvider } from 'react-hook-form';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useIsDealstackSupport } from '@dealstack/frontend/shared/hooks/useIsDealstackSupport';
import { CURRENCY } from '@dealstack/shared/lib/format/currency/currency.ts';
import {
	CREATE_DEAL_FORM,
	DealUpdateController,
} from '@dealstack/frontend/app/pages/Project/CreateDeal/useDealUpdateController.ts';
import { makeInternalDealTagDropdownOptions } from '@dealstack/frontend/app/pages/Project/CreateDeal/internalDealTag.utils.ts';
import { useOrganizations } from '@dealstack/frontend/app/pages/Organizations/hooks/useOrganizations';
import { OrganizationType } from '@dealstack/shared/lib/generated';
import { StyledOrganizationField } from '@dealstack/frontend/app/pages/Project/CreateDeal/CreateDealForm.styled';

const SHORTLISTED_CURRENCIES = Object.values(CURRENCY);

const ORGANIZATION_PICKER_LIMIT = 100;

interface Props {
	createDealController: DealUpdateController;
	hideSubmitButton?: boolean;
}

export const CreateDealForm = ({ createDealController, hideSubmitButton = false }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tCommonUI = useTranslationCommonUi();
	const { handleSubmit, control } = createDealController?.createDealForm || {};
	const isDealstackSupport = useIsDealstackSupport();

	const internalDealTagOptions = useMemo(
		() => makeInternalDealTagDropdownOptions(tCommonUI),
		[tCommonUI]
	);

	const { data: organizations } = useOrganizations(
		{ page: 1, limit: ORGANIZATION_PICKER_LIMIT },
		{ enabled: isDealstackSupport }
	);

	// Only a Sponsor can hold the defaults a deal inherits, so only a Sponsor can be its lead.
	const organizationOptions = useMemo(
		() =>
			(organizations?.rows ?? []).flatMap((organization) =>
				organization.id && organization.type === OrganizationType.SPONSOR
					? [{ value: organization.id, label: organization.name }]
					: []
			),
		[organizations]
	);

	return (
		<FormProvider {...createDealController.createDealForm}>
			<form onSubmit={handleSubmit?.(createDealController.onSubmit)}>
				<Flex direction="column" gap="0.5rem" className="fields-wrapper">
					<TextInput
						data-autofocus
						name={CREATE_DEAL_FORM.DEAL_NAME}
						control={control}
						size="md"
						placeholder={tAdminDashboard('create_project.project_field.placeholder')}
						label={tAdminDashboard('create_project.project_field.label')}
						data-testid={
							TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_CREATE_PROJECT_MODAL_DEAL_NAME_INPUT
						}
					/>
					<TextInput
						name={CREATE_DEAL_FORM.TARGET_NAME}
						control={control}
						size="md"
						placeholder={tAdminDashboard('create_project.target_field.placeholder')}
						label={tAdminDashboard('create_project.target_field.label')}
						data-testid={
							TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_CREATE_PROJECT_MODAL_TARGET_NAME_INPUT
						}
					/>

					<Select
						data={SHORTLISTED_CURRENCIES}
						control={control}
						size="md"
						rightSection={<Icon variant="caret-down" size="vlg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						name={CREATE_DEAL_FORM.CURRENCY}
						placeholder={tAdminDashboard('create_project.currency_field.placeholder')}
						label={tAdminDashboard('create_project.currency_field.label')}
						data-testid={
							TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_CREATE_PROJECT_MODAL_DEFAULT_CURRENCY
						}
						sx={{
							'input:focus:not([data-invalid])': {
								boxShadow: 'none',
							},
						}}
					/>

					{isDealstackSupport ? (
						<StyledOrganizationField>
							<Select
								data={organizationOptions}
								control={control}
								size="md"
								rightSection={<Icon variant="caret-down" size="vlg" />}
								rightSectionProps={{ style: { pointerEvents: 'none' } }}
								name={CREATE_DEAL_FORM.ORGANIZATION_ID}
								placeholder={tAdminDashboard(
									'create_project.organization_field.placeholder'
								)}
								label={tAdminDashboard('create_project.organization_field.label')}
								data-testid={
									TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_CREATE_PROJECT_MODAL_ORGANIZATION
								}
							/>
						</StyledOrganizationField>
					) : null}

					{isDealstackSupport ? (
						<Select
							data={internalDealTagOptions}
							control={control}
							size="md"
							rightSection={<Icon variant="caret-down" size="vlg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							name={CREATE_DEAL_FORM.INTERNAL_DEAL_TAG}
							placeholder={tAdminDashboard(
								'create_project.internal_deal_tag_field.placeholder'
							)}
							label={tAdminDashboard('create_project.internal_deal_tag_field.label')}
							data-testid={
								TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_CREATE_PROJECT_MODAL_INTERNAL_DEAL_TAG
							}
							sx={{
								'input:focus:not([data-invalid])': {
									boxShadow: 'none',
								},
							}}
						/>
					) : null}

					{hideSubmitButton ? null : (
						<Button
							type="submit"
							rightSection={<Icon variant="arrow-right" size="lg" />}
							disabled={
								createDealController.createDealRequest.isPending ||
								createDealController.isSettingUp
							}
							mt="0.75rem"
							data-testid={
								TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_CREATE_PROJECT_MODAL_SUBMIT_BUTTON
							}
						>
							{tAdminDashboard('create_project.create_button')}
						</Button>
					)}
				</Flex>
			</form>
		</FormProvider>
	);
};
